<template>
    <UserLayout :page-title="t('earnings')" :page-section="t('financials')">

        <div class="earn-wrap">
            <div class="earn-inner">

                <!-- ── WELCOME STRIP ── -->
                <div class="earn-welcome anim-up" style="--d:0ms">
                    <div class="ew-left">
                        <div class="ew-icon">
                            <svg width="18" height="18" fill="none" stroke="#a75ded" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <div>
                            <p class="ew-eyebrow">{{ t('financials') }}</p>
                            <p class="ew-title">{{ t('earnings') }}</p>
                        </div>
                    </div>
                    <div class="ew-chips">
                        <div class="ew-chip">
                            <span class="ew-chip-l">{{ t('total') }}</span>
                            <span class="ew-chip-v" :style="`color:${themeColors.primary}`">₿{{ formatBtcAmount(earningsStats.totalEarnings, 12) }}</span>
                        </div>
                        <div class="ew-chip">
                            <span class="ew-chip-l">{{ t('growth') }}</span>
                            <span class="ew-chip-v ew-chip-up">+{{ earningsStats.monthlyGrowth }}%</span>
                        </div>
                        <div class="ew-chip">
                            <span class="ew-chip-l">{{ t('referrals') }}</span>
                            <span class="ew-chip-v" style="color:#e879f9">{{ earningsStats.totalReferrals }}</span>
                        </div>
                    </div>
                </div>

                <!-- ── STAT CARDS ── -->
                <div class="earn-stats">
                    <!-- Total Earnings -->
                    <div class="earn-stat anim-up" style="--d:60ms;--ac:167,93,237">
                        <div class="es-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(167,93,237,0.2),transparent 70%)"></div>
                        <div class="es-top">
                            <div class="es-icon" style="background:rgba(167,93,237,0.15);box-shadow:0 0 12px rgba(167,93,237,0.2)">
                                <svg width="15" height="15" fill="none" stroke="#a75ded" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </div>
                            <span class="es-lbl">{{ t('totalEarnings') }}</span>
                        </div>
                        <p class="es-val" :style="`color:${themeColors.primary}`">₿{{ formatBtcAmount(earningsStats.totalEarnings, 12) }}</p>
                        <div class="es-sub" style="color:#a75ded">
                            ≈ {{ currencySymbol }}{{ formatCurrency(earningsStats.totalEarningsUsd) }}
                            <span style="color:#6b7280"> · </span>
                            +{{ earningsStats.monthlyGrowth }}% <span style="color:#374151">{{ t('thisMonth') }}</span>
                        </div>
                        <div class="es-bar-track"><div class="es-bar shimmer-bar" style="width:82%;background:linear-gradient(90deg,#7c3aed,#a75ded,#c084fc)"></div></div>
                    </div>

                    <!-- Investment Profit -->
                    <div class="earn-stat anim-up" style="--d:100ms;--ac:34,211,238">
                        <div class="es-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(34,211,238,0.15),transparent 70%)"></div>
                        <div class="es-top">
                            <div class="es-icon" style="background:rgba(34,211,238,0.12);box-shadow:0 0 12px rgba(34,211,238,0.18)">
                                <svg width="15" height="15" fill="none" stroke="#22d3ee" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                                </svg>
                            </div>
                            <span class="es-lbl">{{ t('investmentProfit') }}</span>
                        </div>
                        <p class="es-val" style="color:#22d3ee">₿{{ formatBtcAmount(earningsStats.investmentProfitBtc, 12) }}</p>
                        <div class="es-sub" style="color:#22d3ee">{{ earningsStats.activeInvestments }} {{ t('plans') }}</div>
                        <div class="es-bar-track"><div class="es-bar shimmer-bar" style="width:64%;background:linear-gradient(90deg,#0891b2,#22d3ee,#67e8f9)"></div></div>
                    </div>

                    <!-- Referral Commission -->
                    <div class="earn-stat anim-up" style="--d:140ms;--ac:232,121,249">
                        <div class="es-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(232,121,249,0.15),transparent 70%)"></div>
                        <div class="es-top">
                            <div class="es-icon" style="background:rgba(232,121,249,0.12);box-shadow:0 0 12px rgba(232,121,249,0.18)">
                                <svg width="15" height="15" fill="none" stroke="#e879f9" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                                </svg>
                            </div>
                            <span class="es-lbl">{{ t('referralCommission') }}</span>
                        </div>
                        <p class="es-val">{{ currencySymbol }}{{ formatCurrency(earningsStats.referralCommission) }}</p>
                        <div class="es-sub" style="color:#e879f9">{{ earningsStats.totalReferrals }} {{ t('referrals') }}</div>
                        <div class="es-bar-track"><div class="es-bar shimmer-bar" style="width:48%;background:linear-gradient(90deg,#a21caf,#e879f9,#f0abfc)"></div></div>
                    </div>

                    <!-- Staking Rewards -->
                    <div class="earn-stat anim-up" style="--d:180ms;--ac:16,185,129">
                        <div class="es-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(16,185,129,0.15),transparent 70%)"></div>
                        <div class="es-top">
                            <div class="es-icon" style="background:rgba(16,185,129,0.12);box-shadow:0 0 12px rgba(16,185,129,0.18)">
                                <svg width="15" height="15" fill="none" stroke="#10b981" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
                                </svg>
                            </div>
                            <span class="es-lbl">{{ t('stakingRewards') }}</span>
                        </div>
                        <p class="es-val" style="color:#10b981">₿{{ formatBtcAmount(earningsStats.stakingRewards, 12) }}</p>
                        <div class="es-sub" style="color:#10b981">{{ earningsStats.activeStakes }} {{ t('stakes') }}</div>
                        <div class="es-bar-track"><div class="es-bar shimmer-bar" style="width:36%;background:linear-gradient(90deg,#059669,#10b981,#34d399)"></div></div>
                    </div>

                    <!-- Rio Earnings -->
                    <div v-if="earningsStats && Object.prototype.hasOwnProperty.call(earningsStats, 'totalRioEarnings')" class="earn-stat anim-up" style="--d:220ms;--ac:255,111,0">
                        <div class="es-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(255,111,0,0.2),transparent 70%)"></div>
                        <div class="es-top">
                            <div class="es-icon" style="background:rgba(255,111,0,0.15);box-shadow:0 0 12px rgba(255,111,0,0.2)">
                                <svg width="15" height="15" fill="none" stroke="#FF6F00" stroke-width="2" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="9"/>
                                    <path d="M12 6v6l4 2"/>
                                </svg>
                            </div>
                            <span class="es-lbl">Rio Earnings ₿</span>
                        </div>
                        <p class="es-val" style="color:#FF6F00">₿{{ formatBtcAmount(earningsStats.totalRioEarnings, 12) }}</p>
                        <div class="es-sub" style="color:#FF6F00">+₿{{ formatBtcAmount(earningsStats.thisMonthRioEarnings, 12) }} <span style="color:#374151">{{ t('thisMonth') }}</span></div>
                        <div class="es-bar-track"><div class="es-bar shimmer-bar" style="width:52%;background:linear-gradient(90deg,#ff6f00,#ffb74d,#ffe0b2)"></div></div>
                    </div>
                </div>

                <!-- ── MID ROW: Breakdown · Quick Stats · Summary ── -->
                <div class="earn-mid anim-up" style="--d:240ms">

                    <!-- Monthly Breakdown -->
                    <div class="earn-card">
                        <div class="earn-card-head">
                            <div class="earn-card-icon-wrap" :style="`background:rgba(${themeColors.primaryRgb},0.12);border-color:rgba(${themeColors.primaryRgb},0.22);`">
                                <svg width="13" height="13" fill="none" :stroke="themeColors.primary" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                                </svg>
                            </div>
                            <div>
                                <h3 class="earn-title">{{ t('monthlyBreakdown') }}</h3>
                                <p class="earn-sub">{{ t('thisMonth') }}</p>
                            </div>
                        </div>

                        <!-- Investment Profits bar -->
                        <div class="earn-breakdown-item" :style="`background:rgba(${themeColors.primaryRgb},0.06);border-color:rgba(${themeColors.primaryRgb},0.18);`">
                            <div class="ebi-header">
                                <div class="ebi-label-wrap">
                                    <svg width="11" height="11" fill="none" :stroke="themeColors.primary" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>
                                    <span class="ebi-label">{{ t('investmentProfits') }}</span>
                                </div>
                                <span class="ebi-val" :style="`color:${themeColors.primary}`">{{ currencySymbol }}{{ formatCurrency(monthlyBreakdown.investment) }}</span>
                            </div>
                            <div class="ebi-track">
                                <div class="ebi-fill shimmer-bar"
                                     :style="`width:${getProgressPercentage(monthlyBreakdown.investment)}%;background:linear-gradient(90deg,${themeColors.primaryDark},${themeColors.primary});`"></div>
                            </div>
                        </div>

                        <!-- Referral Commission bar -->
                        <div class="earn-breakdown-item" style="background:rgba(232,121,249,0.06);border-color:rgba(232,121,249,0.18);">
                            <div class="ebi-header">
                                <div class="ebi-label-wrap">
                                    <svg width="11" height="11" fill="none" stroke="#e879f9" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                                    <span class="ebi-label">{{ t('referralCommissions') }}</span>
                                </div>
                                <span class="ebi-val" style="color:#e879f9">{{ currencySymbol }}{{ formatCurrency(monthlyBreakdown.referral) }}</span>
                            </div>
                            <div class="ebi-track">
                                <div class="ebi-fill shimmer-bar" :style="`width:${getProgressPercentage(monthlyBreakdown.referral)}%;background:linear-gradient(90deg,#a21caf,#e879f9);`"></div>
                            </div>
                        </div>

                        <!-- Staking Rewards bar -->
                        <div class="earn-breakdown-item" style="background:rgba(16,185,129,0.06);border-color:rgba(16,185,129,0.18);">
                            <div class="ebi-header">
                                <div class="ebi-label-wrap">
                                    <svg width="11" height="11" fill="none" stroke="#10b981" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>
                                    <span class="ebi-label">{{ t('stakingRewards') }}</span>
                                </div>
                                <span class="ebi-val" style="color:#10b981">{{ currencySymbol }}{{ formatCurrency(monthlyBreakdown.staking) }}</span>
                            </div>
                            <div class="ebi-track">
                                <div class="ebi-fill shimmer-bar" :style="`width:${getProgressPercentage(monthlyBreakdown.staking)}%;background:linear-gradient(90deg,#059669,#10b981);`"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Quick Stats -->
                    <div class="earn-card">
                        <div class="earn-card-head">
                            <div class="earn-card-icon-wrap" style="background:rgba(34,211,238,0.12);border-color:rgba(34,211,238,0.22);">
                                <svg width="13" height="13" fill="none" stroke="#22d3ee" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                                </svg>
                            </div>
                            <div>
                                <h3 class="earn-title">{{ t('quickStats') }}</h3>
                                <p class="earn-sub">{{ t('performanceSummary') || 'Performance' }}</p>
                            </div>
                        </div>

                        <div class="earn-stat-rows">
                            <div class="earn-stat-row-item">
                                <div class="esri-left">
                                    <svg width="12" height="12" fill="none" stroke="#6b7280" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                    <span class="esri-label">{{ t('avgDaily') }}</span>
                                </div>
                                <span class="esri-val">{{ currencySymbol }}{{ formatCurrency(quickStats.avgDailyEarnings) }}</span>
                            </div>
                            <div class="earn-stat-row-item">
                                <div class="esri-left">
                                    <svg width="12" height="12" fill="none" stroke="#6b7280" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                    <span class="esri-label">{{ t('thisWeek') }}</span>
                                </div>
                                <span class="esri-val">{{ currencySymbol }}{{ formatCurrency(quickStats.thisWeek) }}</span>
                            </div>
                            <div class="earn-stat-row-item">
                                <div class="esri-left">
                                    <svg width="12" height="12" fill="none" stroke="#6b7280" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                    <span class="esri-label">{{ t('thisMonth') }}</span>
                                </div>
                                <span class="esri-val">{{ currencySymbol }}{{ formatCurrency(quickStats.thisMonth) }}</span>
                            </div>
                            <div class="earn-stat-row-item earn-stat-row-item--hi" :style="`background:rgba(${themeColors.primaryRgb},0.07);border-color:rgba(${themeColors.primaryRgb},0.18);`">
                                <div class="esri-left">
                                    <svg width="12" height="12" fill="none" :stroke="themeColors.primary" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
                                    <span class="esri-label" style="color:#fff;font-weight:700">{{ t('allTime') }}</span>
                                </div>
                                <span class="esri-val" :style="`color:${themeColors.primary};font-size:15px`">{{ currencySymbol }}{{ formatCurrency(quickStats.allTime) }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Transaction Summary -->
                    <div class="earn-card">
                        <div class="earn-card-head">
                            <div class="earn-card-icon-wrap" style="background:rgba(251,191,36,0.12);border-color:rgba(251,191,36,0.22);">
                                <svg width="13" height="13" fill="none" stroke="#fbbf24" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                </svg>
                            </div>
                            <div>
                                <h3 class="earn-title">{{ t('transactionSummary') }}</h3>
                                <p class="earn-sub">{{ t('allTime') }}</p>
                            </div>
                        </div>

                        <div class="earn-stat-rows">
                            <div class="earn-stat-row-item">
                                <div class="esri-left">
                                    <svg width="12" height="12" fill="none" :stroke="themeColors.primary" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
                                    <span class="esri-label">{{ t('totalCredits') }}</span>
                                </div>
                                <span class="esri-val" :style="`color:${themeColors.primary}`">{{ transactionSummary.totalCredits }}</span>
                            </div>
                            <div class="earn-stat-row-item">
                                <div class="esri-left">
                                    <svg width="12" height="12" fill="none" stroke="#ef4444" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M18 12H6"/></svg>
                                    <span class="esri-label">{{ t('totalDebits') }}</span>
                                </div>
                                <span class="esri-val" style="color:#ef4444">{{ transactionSummary.totalDebits }}</span>
                            </div>
                            <div class="earn-stat-row-item">
                                <div class="esri-left">
                                    <svg width="12" height="12" fill="none" stroke="#6b7280" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                    <span class="esri-label">{{ t('totalFees') }}</span>
                                </div>
                                <span class="esri-val">{{ currencySymbol }}{{ formatCurrency(transactionSummary.totalFees) }}</span>
                            </div>
                            <div class="earn-stat-row-item earn-stat-row-item--hi" :style="`background:rgba(${themeColors.primaryRgb},0.07);border-color:rgba(${themeColors.primaryRgb},0.18);`">
                                <div class="esri-left">
                                    <svg width="13" height="13" fill="none" :stroke="themeColors.primary" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/></svg>
                                    <span class="esri-label" style="color:#fff;font-weight:700">{{ t('netEarnings') }}</span>
                                </div>
                                <span class="esri-val" :style="`color:${themeColors.primary};font-size:15px`">{{ currencySymbol }}{{ formatCurrency(transactionSummary.netEarnings) }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ── TRANSACTION HISTORY ── -->
                <div class="earn-hist-card anim-up" style="--d:340ms">
                    <!-- Header -->
                    <div class="earn-hist-head">
                        <div class="earn-hist-head-left">
                            <div class="earn-card-icon-wrap" :style="`background:rgba(${themeColors.primaryRgb},0.12);border-color:rgba(${themeColors.primaryRgb},0.22);`">
                                <svg width="13" height="13" fill="none" :stroke="themeColors.primary" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                                </svg>
                            </div>
                            <div>
                                <h2 class="earn-title" style="font-size:14px">{{ t('transactionLogs') }}</h2>
                                <p class="earn-sub">{{ t('completeTransactionHistory') }}</p>
                            </div>
                        </div>
                        <!-- Filter pills -->
                        <div class="earn-filters">
                            <button
                                v-for="filter in ['all','credit','debit','bonus','stake','unstake','staking_reward']"
                                :key="filter"
                                class="earn-filter-btn"
                                :class="filterType === filter ? 'earn-filter-btn--on' : ''"
                                :style="filterType === filter ? `background:${themeColors.primary};color:#0f0617;border-color:${themeColors.primary};` : ''"
                                @click="changeFilter(filter)">
                                {{ filter === 'all' ? t('all') : formatTransactionType(filter) }}
                            </button>
                        </div>
                    </div>

                    <!-- Desktop Table -->
                    <div class="earn-table-wrap">
                        <table class="earn-table">
                            <thead>
                            <tr>
                                <th>{{ t('transaction') }}</th>
                                <th>{{ t('type') }}</th>
                                <th>{{ t('amount') }}</th>
                                <th>{{ t('fee') }}</th>
                                <th>{{ t('balance') }}</th>
                                <th>{{ t('status') }}</th>
                                <th>{{ t('date') }}</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="!paginatedTransactions || paginatedTransactions.length === 0">
                                <td colspan="8">
                                    <div class="earn-empty">
                                        <div class="earn-empty-ic">
                                            <svg width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                        </div>
                                        <p class="earn-empty-t">{{ t('noTransactionsFound') }}</p>
                                        <p class="earn-empty-s">{{ t('noTransactionHistory') }}</p>
                                    </div>
                                </td>
                            </tr>
                            <tr v-for="tx in paginatedTransactions" :key="tx.id" class="earn-tr">
                                <td>
                                    <div class="et-tx-wrap">
                                        <div class="et-tx-icon" :style="`background:rgba(${themeColors.primaryRgb},0.12);`">
                                            <svg width="13" height="13" fill="none" :stroke="themeColors.primary" stroke-width="2" viewBox="0 0 24 24">
                                                <path v-if="['credit','bonus','staking_reward','deposit'].includes(tx.type)" stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                                                <path v-else stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <p class="et-txid">{{ tx.transaction_id }}</p>
                                            <p v-if="tx.details" class="et-txsub">{{ getTransactionPreview(tx) }}</p>
                                        </div>
                                    </div>
                                </td>
                                <td><span class="earn-type-badge">{{ formatTransactionType(tx.type) }}</span></td>
                                <td>
                                    <span class="et-amount"
                                          :style="['credit','bonus','staking_reward','deposit'].includes(tx.type) ? `color:${themeColors.primary}` : 'color:#ef4444'">
                                        {{ ['credit','bonus','staking_reward','deposit'].includes(tx.type) ? '+' : '-' }}{{ formatTransactionAmount(tx) }}
                                    </span>
                                </td>
                                <td><span class="et-fee">{{ formatTransactionFee(tx) }}</span></td>
                                <td><span class="et-bal">{{ formatTransactionBalance(tx, tx.post_balance) }}</span></td>
                                <td>
                                    <span class="earn-status-badge" :style="getStatusStyle(tx.status)">{{ formatStatus(tx.status) }}</span>
                                </td>
                                <td>
                                    <p class="et-date">{{ formatDate(tx.created_at) }}</p>
                                    <p class="et-time">{{ formatTime(tx.created_at) }}</p>
                                </td>
                                <td>
                                    <button class="earn-view-btn" :style="`color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.28);background:rgba(${themeColors.primaryRgb},0.08);`"
                                            @click="showTransactionDetails(tx)">{{ t('viewDetails') }}</button>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Mobile cards -->
                    <div class="earn-mob-list">
                        <div v-if="!paginatedTransactions || paginatedTransactions.length === 0" class="earn-empty" style="padding:40px 16px">
                            <div class="earn-empty-ic">
                                <svg width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                            </div>
                            <p class="earn-empty-t">{{ t('noTransactionsFound') }}</p>
                            <p class="earn-empty-s">{{ t('noTransactionHistory') }}</p>
                        </div>

                        <div v-for="tx in paginatedTransactions" :key="tx.id" class="earn-mob-item">
                            <div class="emi-top">
                                <div class="emi-id-wrap">
                                    <p class="emi-txid">{{ tx.transaction_id }}</p>
                                    <p class="emi-type">{{ formatTransactionType(tx.type) }}</p>
                                </div>
                                <span class="earn-status-badge" :style="getStatusStyle(tx.status)">{{ formatStatus(tx.status) }}</span>
                            </div>
                            <div class="emi-grid">
                                <div class="emi-cell">
                                    <p class="emi-cell-lbl">{{ t('amount') }}</p>
                                    <p class="emi-cell-val"
                                       :style="['credit','bonus','staking_reward','deposit'].includes(tx.type) ? `color:${themeColors.primary}` : 'color:#ef4444'">
                                        {{ ['credit','bonus','staking_reward','deposit'].includes(tx.type) ? '+' : '-' }}{{ formatTransactionAmount(tx) }}
                                    </p>
                                </div>
                                <div class="emi-cell">
                                    <p class="emi-cell-lbl">{{ t('balance') }}</p>
                                    <p class="emi-cell-val" style="color:#fff">{{ formatTransactionBalance(tx, tx.post_balance) }}</p>
                                </div>
                            </div>
                            <div class="emi-foot">
                                <span class="emi-date">{{ formatDate(tx.created_at) }} {{ formatTime(tx.created_at) }}</span>
                                <span class="emi-fee">{{ t('fee') }}: {{ formatTransactionFee(tx) }}</span>
                            </div>
                            <button class="earn-mob-view-btn"
                                    :style="`color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.28);background:rgba(${themeColors.primaryRgb},0.07);`"
                                    @click="showTransactionDetails(tx)">
                                {{ t('viewDetails') }}
                            </button>
                        </div>
                    </div>

                    <!-- Pagination -->
                    <div class="earn-pagination">
                        <p class="earn-pag-info">
                            {{ t('showing') }}
                            <strong>{{ ((currentPage-1)*itemsPerPage)+1 }}–{{ Math.min(currentPage*itemsPerPage, filteredTransactions.length) }}</strong>
                            {{ t('of') }} <strong>{{ filteredTransactions.length }}</strong>
                        </p>
                        <div class="earn-pag-btns">
                            <button class="earn-pag-btn" :disabled="currentPage===1"
                                    :class="currentPage===1 ? 'earn-pag-btn--dis' : ''"
                                    @click="goToPage(currentPage-1)">{{ t('previous') }}</button>
                            <button v-for="pg in visiblePages" :key="pg"
                                    class="earn-pag-btn earn-pag-num"
                                    :style="pg===currentPage ? `background:${themeColors.primary};color:#0f0617;border-color:${themeColors.primary};` : ''"
                                    @click="goToPage(pg)">{{ pg }}</button>
                            <button class="earn-pag-btn" :disabled="currentPage===totalPages"
                                    :class="currentPage===totalPages ? 'earn-pag-btn--dis' : ''"
                                    @click="goToPage(currentPage+1)">{{ t('next') }}</button>
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <!-- ── TRANSACTION DETAIL MODAL ── -->
        <Transition name="earn-fade">
            <div v-if="showModal" class="earn-overlay" @click.self="closeModal">
                <Transition name="earn-pop">
                    <div v-if="showModal" class="earn-modal" role="dialog" aria-modal="true">

                        <!-- Modal header -->
                        <div class="earn-modal-head">
                            <h3 class="earn-modal-title">{{ t('transactionDetails') }}</h3>
                            <button class="earn-modal-close" @click="closeModal" aria-label="Close">
                                <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                            </button>
                        </div>

                        <div v-if="selectedTransaction" class="earn-modal-body">

                            <!-- Transaction info -->
                            <div class="earn-modal-section">
                                <div class="ems-dot" :style="`background:${themeColors.primary}`"></div>
                                <h4 class="ems-title">{{ t('transactionInformation') }}</h4>
                                <div class="ems-grid">
                                    <div class="ems-field">
                                        <label class="ems-lbl">{{ t('transactionId') }}</label>
                                        <p class="ems-mono" :style="`color:${themeColors.primary}`">{{ selectedTransaction.transaction_id }}</p>
                                    </div>
                                    <div class="ems-field">
                                        <label class="ems-lbl">{{ t('status') }}</label>
                                        <span class="earn-status-badge" :style="getStatusStyle(selectedTransaction.status)">{{ formatStatus(selectedTransaction.status) }}</span>
                                    </div>
                                    <div class="ems-field">
                                        <label class="ems-lbl">{{ t('type') }}</label>
                                        <p class="ems-val">{{ formatTransactionType(selectedTransaction.type) }}</p>
                                    </div>
                                    <div class="ems-field">
                                        <label class="ems-lbl">{{ t('date') }}</label>
                                        <p class="ems-val">{{ formatDateTime(selectedTransaction.created_at) }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Amount details -->
                            <div class="earn-modal-section">
                                <div class="ems-dot" :style="`background:${themeColors.primary}`"></div>
                                <h4 class="ems-title">{{ t('amountDetails') }}</h4>
                                <div class="ems-amounts">
                                    <div class="ems-amount-row">
                                        <span class="ems-lbl">{{ t('amount') }}:</span>
                                        <span :style="['credit','bonus','staking_reward','deposit'].includes(selectedTransaction.type) ? `color:${themeColors.primary};font-weight:700` : 'color:#ef4444;font-weight:700'">
                                                {{ ['credit','bonus','staking_reward','deposit'].includes(selectedTransaction.type) ? '+' : '-' }}{{ formatTransactionAmount(selectedTransaction) }}
                                        </span>
                                    </div>
                                    <div class="ems-amount-row">
                                        <span class="ems-lbl">{{ t('fee') }}:</span>
                                        <span style="color:#fff;font-weight:600">{{ formatTransactionFee(selectedTransaction) }}</span>
                                    </div>
                                    <div class="ems-amount-row ems-amount-row--hi" :style="`background:rgba(${themeColors.primaryRgb},0.07);border-top:1px solid rgba(${themeColors.primaryRgb},0.18);`">
                                        <span style="color:#fff;font-weight:700">{{ t('postBalance') }}:</span>
                                        <span :style="`color:${themeColors.primary};font-weight:900;font-size:16px`">{{ formatTransactionBalance(selectedTransaction, selectedTransaction.post_balance) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Additional details -->
                            <div v-if="selectedTransaction.details" class="earn-modal-section">
                                <div class="ems-dot" :style="`background:${themeColors.primary}`"></div>
                                <h4 class="ems-title">{{ t('additionalDetails') }}</h4>
                                <div v-if="formattedDetailItems.length > 0" class="ems-detail-list">
                                    <div v-for="item in formattedDetailItems" :key="item.key" class="ems-detail-row">
                                        <span class="ems-detail-key">{{ item.label }}</span>
                                        <span class="ems-detail-val">{{ item.value }}</span>
                                    </div>
                                </div>
                                <p v-else class="ems-raw">{{ selectedTransaction.details }}</p>
                            </div>
                        </div>

                        <div class="earn-modal-foot">
                            <button class="earn-modal-close-btn" @click="closeModal">{{ t('close') }}</button>
                        </div>
                    </div>
                </Transition>
            </div>
        </Transition>

    </UserLayout>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import {usePage} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';
import useCurrency from "@/composables/useCurrency.js";

const props = defineProps({
    earningsStats: {type: Object, required: true},
    transactions: {type: Array, default: () => []},
    monthlyBreakdown: {type: Object, required: true},
    quickStats: {type: Object, required: true},
    transactionSummary: {type: Object, required: true}
});

const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();
const { currencySymbols } = useCurrency();

const filterType = ref('all');
const currentPage = ref(1);
const itemsPerPage = 10;
const showModal = ref(false);
const selectedTransaction = ref(null);

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const currencySymbol = computed(() => page.props.currencySymbol || '$');
const btcPriceUsd = computed(() => parseFloat(page.props.btcPriceUsd || 0));

const getCurrencyCode = (currency) => {
    if (!currency || typeof currency !== 'string') return '';
    return currency.trim().toUpperCase();
};

const getTransactionCurrency = (tx) => {
    if (!tx || typeof tx !== 'object') return '';
    if (tx.currency) return tx.currency;
    if (tx.wallet_type === 'rio') return 'BTC';
    return '';
};

const getCurrencySymbol = (currency) => {
    const code = getCurrencyCode(currency);
    if (!code) return currencySymbol.value;
    return currencySymbols[code] || `${code} `;
};

const getCurrencyPrecision = (currency) => {
    const precisionMap = {
        BTC: 8,
        ETH: 8,
        BCH: 8,
        LTC: 8,
        XRP: 8,
        ADA: 8,
        DOT: 8,
        BNB: 8,
        SOL: 8,
        AVAX: 8,
        MATIC: 8,
        TRX: 8,
        USDT: 6,
        USDC: 6,
        BUSD: 6,
        DAI: 6,
        TUSD: 6,
        USDP: 6,
        FRAX: 6,
        LUSD: 6,
        USDD: 6,
    };

    return precisionMap[getCurrencyCode(currency)] ?? 2;
};

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return {r: 134, g: 239, b: 172};
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172};
};

const rgbToHex = (r, g, b) => "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    return rgbToHex(
        Math.min(255, Math.max(0, rgb.r + amount)),
        Math.min(255, Math.max(0, rgb.g + amount)),
        Math.min(255, Math.max(0, rgb.b + amount))
    );
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const toBtc = (amount) => {
    const usd = parseFloat(amount) || 0;
    const price = btcPriceUsd.value;
    return price > 0 ? usd / price : usd;
};

const formatBtcAmount = (amount, precision = 8) => new Intl.NumberFormat('en-US', { minimumFractionDigits: precision, maximumFractionDigits: precision }).format(parseFloat(amount) || 0);
const getRawBtcFromDetails = (details) => {
    if (!details || typeof details !== 'string') return null;
    const match = details.match(/Raw BTC:\s*([0-9.]+)/i);
    if (!match) return null;
    const value = parseFloat(match[1]);
    return Number.isNaN(value) ? null : value;
};
const formatAmountWithPrecision = (amount, currency) => new Intl.NumberFormat('en-US', {
    minimumFractionDigits: getCurrencyPrecision(currency),
    maximumFractionDigits: getCurrencyPrecision(currency)
}).format(Math.abs(parseFloat(amount) || 0));
const formatTransactionAmount = (tx) => {
    const currency = getTransactionCurrency(tx);
    if (currency === 'BTC') {
        const rawBtc = getRawBtcFromDetails(tx?.details);
        const precision = rawBtc !== null ? 12 : getCurrencyPrecision(currency);
        return `₿${formatBtcAmount(rawBtc !== null ? rawBtc : tx?.amount, precision)}`;
    }

    return `${getCurrencySymbol(currency)}${formatAmountWithPrecision(tx?.amount, currency)}`;
};
const formatTransactionFee = (tx) => {
    const currency = getTransactionCurrency(tx);
    return `${getCurrencySymbol(currency)}${formatAmountWithPrecision(tx?.fee, currency)}`;
};
const formatTransactionBalance = (tx, amount) => {
    const currency = getTransactionCurrency(tx);
    return `${getCurrencySymbol(currency)}${formatAmountWithPrecision(amount, currency)}`;
};

const parseTransactionDetails = (details, detailsData = null) => {
    if (detailsData && typeof detailsData === 'object') return detailsData;
    if (!details) return null;
    if (typeof details === 'object' && details !== null) return details;
    if (typeof details !== 'string') return null;
    try {
        const parsed = JSON.parse(details);
        return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch {
        return null;
    }
};

const toTitleCase = (value) => {
    if (!value || typeof value !== 'string') return '';
    return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const detailLabelMap = {referral_uid: 'Referral UID', referral_id: 'Referral ID'};

const formatMoneyValue = (amount) => `${currencySymbol.value}${formatCurrency(amount)}`;

const formatDetailValue = (key, value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (key === 'subtype' && typeof value === 'string') {
        const subtypeMap = {direct_referral_bonus: 'Direct Referral Bonus', binary_pairing_bonus: 'Binary Pairing Bonus', token_credit_allocation: 'DS-Credits Allocation'};
        return subtypeMap[value] || toTitleCase(value);
    }
    if (['package_amount', 'matched_volume', 'weaker_leg_volume', 'reward_amount'].includes(key)) return formatMoneyValue(value);
    if (['bonus_percentage', 'reward_percentage'].includes(key)) {
        const num = parseFloat(value);
        return Number.isNaN(num) ? `${value}%` : `${num}%`;
    }
    if (key.endsWith('_at') && typeof value === 'string') return formatDateTime(value);
    return String(value);
};

const getTransactionPreview = (transaction) => {
    const parsed = parseTransactionDetails(transaction?.details, transaction?.details_data);
    if (!parsed) return String(transaction?.details || '');
    const subtype = parsed.subtype || '';
    if (subtype === 'direct_referral_bonus') {
        const referralName = parsed.referral_name || 'Referral';
        const referralUid = (parsed.referral_uid !== undefined && parsed.referral_uid !== null && parsed.referral_uid !== '') ? String(parsed.referral_uid) : null;
        const bonusPct = parsed.bonus_percentage !== undefined ? `${parsed.bonus_percentage}%` : '';
        return `Direct referral bonus from ${referralName}${referralUid !== null ? ` (Referral UID: ${referralUid})` : ''}${bonusPct ? ` (${bonusPct})` : ''}`;
    }
    if (subtype === 'binary_pairing_bonus') {
        const matched = parsed.matched_volume !== undefined ? formatMoneyValue(parsed.matched_volume) : null;
        const bonusPct = parsed.bonus_percentage !== undefined ? `${parsed.bonus_percentage}%` : null;
        if (matched && bonusPct) return `Binary pairing bonus: ${bonusPct} on ${matched} matched volume`;
        return 'Binary pairing bonus';
    }
    if (parsed.description) return String(parsed.description);
    return toTitleCase(subtype) || String(transaction?.details || '');
};

const formattedDetailItems = computed(() => {
    const parsed = parseTransactionDetails(selectedTransaction.value?.details, selectedTransaction.value?.details_data);
    if (!parsed) return [];
    const hasReferralUid = Object.prototype.hasOwnProperty.call(parsed, 'referral_uid') && parsed.referral_uid !== null && parsed.referral_uid !== '';
    const keyOrder = ['subtype','description','referral_name','referral_uid','referral_id','package_amount','bonus_percentage','weaker_leg','matched_volume','weaker_leg_volume','reward_percentage','reward_amount'];
    const orderedKeys = [
        ...keyOrder.filter((key) => Object.prototype.hasOwnProperty.call(parsed, key)),
        ...Object.keys(parsed).filter((key) => !keyOrder.includes(key)),
    ].filter((key) => !(hasReferralUid && key === 'referral_id'));
    return orderedKeys.map((key) => ({key, label: detailLabelMap[key] || toTitleCase(key), value: formatDetailValue(key, parsed[key])}));
});

const filteredTransactions = computed(() => {
    if (filterType.value === 'all') return props.transactions;
    return props.transactions.filter(t => t.type === filterType.value);
});

const paginatedTransactions = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage;
    return filteredTransactions.value.slice(start, start + itemsPerPage);
});

const totalPages = computed(() => Math.ceil(filteredTransactions.value.length / itemsPerPage));

const visiblePages = computed(() => {
    const pages = [];
    for (let i = 1; i <= totalPages.value; i++) {
        if (i === 1 || i === totalPages.value || Math.abs(i - currentPage.value) <= 1) pages.push(i);
    }
    return pages;
});

const totalMonthlyEarnings = computed(() =>
    parseFloat(props.monthlyBreakdown.investment || 0) +
    parseFloat(props.monthlyBreakdown.referral || 0) +
    parseFloat(props.monthlyBreakdown.staking || 0)
);

const getProgressPercentage = (value) => {
    const total = totalMonthlyEarnings.value;
    if (total === 0) return 0;
    const percentage = (parseFloat(value || 0) / total) * 100;
    return value > 0 ? Math.max(5, Math.min(100, percentage)) : 0;
};

const formatCurrency = (amount) => {
    const number = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(number);
};

const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable');
    try { return new Date(dateString).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}); }
    catch { return t('invalidDate'); }
};

const formatTime = (dateString) => {
    if (!dateString) return t('notAvailable');
    try { return new Date(dateString).toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'}); }
    catch { return t('invalidTime'); }
};

const formatDateTime = (dateString) => {
    if (!dateString) return t('notAvailable');
    try { return new Date(dateString).toLocaleString('en-US', {year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'}); }
    catch { return t('invalidDate'); }
};

const formatTransactionType = (type) => {
    const typeMap = {
        'deposit': t('deposit'), 'withdrawal': t('withdrawal'), 'transfer': t('transfer'),
        'credit': t('credit'), 'debit': t('debit'), 'bonus': t('bonus'),
        'stake': t('stake'), 'unstake': t('unstake'), 'staking_reward': t('stakingReward')
    };
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
};

const formatStatus = (status) => {
    if (!status || typeof status !== 'string') return t('unknown');
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getStatusStyle = (status) => {
    if (!status) return {backgroundColor: 'rgba(148,163,184,0.2)', borderColor: 'rgba(148,163,184,0.3)', color: '#94a3b8'};
    switch (status.toLowerCase()) {
        case 'completed': return {backgroundColor: `rgba(${themeColors.value.primaryRgb},0.18)`, borderColor: `rgba(${themeColors.value.primaryRgb},0.35)`, color: themeColors.value.primary};
        case 'pending':   return {backgroundColor: 'rgba(251,191,36,0.18)', borderColor: 'rgba(251,191,36,0.35)', color: '#fbbf24'};
        case 'failed': case 'cancelled': return {backgroundColor: 'rgba(239,68,68,0.18)', borderColor: 'rgba(239,68,68,0.35)', color: '#ef4444'};
        default: return {backgroundColor: 'rgba(148,163,184,0.2)', borderColor: 'rgba(148,163,184,0.3)', color: '#94a3b8'};
    }
};

const showTransactionDetails = (transaction) => {
    if (!transaction?.id) { showToast(t('invalidTransactionSelected'), 'error'); return; }
    selectedTransaction.value = transaction;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    setTimeout(() => { selectedTransaction.value = null; }, 300);
};

const goToPage = (page) => { if (page >= 1 && page <= totalPages.value) currentPage.value = page; };
const changeFilter = (filter) => { filterType.value = filter; currentPage.value = 1; };

onMounted(() => {
    const flash = page.props.flash;
    if (flash?.success) showToast(flash.success, 'success');
    if (flash?.error)   showToast(flash.error,   'error');
    if (flash?.warning) showToast(flash.warning, 'warning');
    if (flash?.info)    showToast(flash.info,    'info');
});
</script>

<style scoped>
/* ══════════════════════════════
   PAGE SHELL
══════════════════════════════ */
.earn-wrap  { position:relative; z-index:10; width:100%; }
.earn-inner { max-width:1280px; margin:0 auto; padding:14px 12px; display:flex; flex-direction:column; gap:12px; }
@media(min-width:640px)  { .earn-inner { padding:18px 18px; gap:14px; } }
@media(min-width:1024px) { .earn-inner { padding:20px 24px; gap:16px; } }

/* ══════════════════════════════
   ENTRANCE ANIMATION
══════════════════════════════ */
.anim-up { animation:slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay:var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

/* shimmer-bar */
.shimmer-bar {
    position:relative; overflow:hidden; border-radius:99px;
}
.shimmer-bar::after {
    content:'';
    position:absolute; inset:0;
    background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.18) 50%,transparent 100%);
    animation:shimmer 2s infinite;
}
@keyframes shimmer { from{transform:translateX(-100%)} to{transform:translateX(100%)} }

/* ══════════════════════════════
   BASE CARD
══════════════════════════════ */
.earn-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.13);
    border-radius: 16px;
    padding: 14px;
    position: relative;
    overflow: hidden;
    transition: border-color 0.25s;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.earn-card::before {
    content:'';
    position:absolute;
    top:0; left:14%; right:14%; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.earn-card:hover { border-color:rgba(167,93,237,0.24); }
@media(min-width:640px) { .earn-card { padding:16px; gap:11px; } }

.earn-card-head {
    display: flex;
    align-items: center;
    gap: 10px;
}
.earn-card-icon-wrap {
    width:30px; height:30px; border-radius:9px;
    display:flex; align-items:center; justify-content:center;
    flex-shrink:0;
    border:1px solid;
}
.earn-title { font-size:13px; font-weight:800; color:#fff; letter-spacing:-0.01em; }
.earn-sub   { font-size:10px; color:#374151; margin-top:1px; }

/* ══════════════════════════════
   WELCOME STRIP
══════════════════════════════ */
.earn-welcome {
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; flex-wrap:wrap;
    padding:12px 16px; border-radius:13px;
    background:rgba(167,93,237,0.06);
    border:1px solid rgba(167,93,237,0.16);
}
.ew-left { display:flex; align-items:center; gap:10px; flex-shrink:0; }
.ew-icon {
    width:40px; height:40px; border-radius:11px;
    background:linear-gradient(135deg,rgba(124,58,237,0.3),rgba(167,93,237,0.2));
    border:1px solid rgba(167,93,237,0.28);
    display:flex; align-items:center; justify-content:center;
    flex-shrink:0;
    box-shadow:0 0 14px rgba(167,93,237,0.22);
}
.ew-eyebrow { font-size:9px; font-weight:700; color:rgba(167,93,237,0.7); text-transform:uppercase; letter-spacing:0.1em; }
.ew-title   { font-size:15px; font-weight:900; color:#fff; letter-spacing:-0.02em; }
.ew-chips   { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.ew-chip {
    display:flex; flex-direction:column; align-items:center; gap:2px;
    padding:6px 12px; border-radius:9px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    min-width:58px;
}
.ew-chip-l  { font-size:8px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.12em; }
.ew-chip-v  { font-size:13px; font-weight:900; color:#fff; }
.ew-chip-up { color:#10b981 !important; }

/* ══════════════════════════════
   STAT CARDS ROW
══════════════════════════════ */
.earn-stats {
    display:grid;
    grid-template-columns: repeat(2,1fr);
    gap:10px;
}
@media(min-width:768px) { .earn-stats { grid-template-columns:repeat(5,1fr); } }

.earn-stat {
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(167,93,237,0.12);
    border-radius:14px;
    padding:12px;
    position:relative; overflow:hidden;
    transition:transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s;
    cursor:default;
}
.earn-stat:hover {
    transform:translateY(-2px);
    box-shadow:0 8px 24px rgba(var(--ac,167,93,237),0.18);
}
.es-glow { position:absolute; inset:0; pointer-events:none; }
.es-top  { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
.es-icon {
    width:30px; height:30px; border-radius:9px;
    display:flex; align-items:center; justify-content:center;
    flex-shrink:0;
    border:1px solid rgba(var(--ac,167,93,237),0.22);
}
.es-lbl  { font-size:10px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.06em; line-height:1.2; }
.es-val  { font-size:18px; font-weight:900; color:#fff; letter-spacing:-0.03em; margin-bottom:3px; line-height:1.1; }
.es-sub  { font-size:10px; font-weight:600; margin-bottom:8px; }
.es-bar-track { height:3px; border-radius:99px; background:rgba(255,255,255,0.06); overflow:hidden; }
.es-bar  { height:100%; border-radius:99px; transition:width 1.2s cubic-bezier(0.34,1,0.64,1); }
@media(min-width:640px) { .earn-stat { padding:14px; } .es-val { font-size:20px; } }

/* ══════════════════════════════
   MID ROW
══════════════════════════════ */
.earn-mid {
    display:grid;
    gap:12px;
}
@media(min-width:768px)  { .earn-mid { grid-template-columns:1fr 1fr; } }
@media(min-width:1024px) { .earn-mid { grid-template-columns:1fr 1fr 1fr; } }

/* ── Breakdown items ── */
.earn-breakdown-item {
    border-radius:11px;
    padding:10px 11px;
    border:1px solid;
    transition:transform 0.2s;
}
.earn-breakdown-item:hover { transform:scale(1.01); }
.ebi-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
.ebi-label-wrap { display:flex; align-items:center; gap:6px; }
.ebi-label { font-size:11px; font-weight:700; color:#d1d5db; }
.ebi-val   { font-size:12px; font-weight:800; }
.ebi-track { height:5px; border-radius:99px; background:rgba(255,255,255,0.07); overflow:hidden; }
.ebi-fill  { height:100%; border-radius:99px; transition:width 1.2s cubic-bezier(0.34,1,0.64,1); }

/* ── Stat row items (quick stats / tx summary) ── */
.earn-stat-rows { display:flex; flex-direction:column; gap:6px; flex:1; }
.earn-stat-row-item {
    display:flex; align-items:center; justify-content:space-between;
    gap:8px; padding:9px 11px; border-radius:10px;
    background:rgba(255,255,255,0.03);
    border:1px solid rgba(255,255,255,0.06);
    transition:background 0.18s;
}
.earn-stat-row-item:hover { background:rgba(255,255,255,0.05); }
.earn-stat-row-item--hi { border-radius:10px; }
.esri-left  { display:flex; align-items:center; gap:7px; }
.esri-label { font-size:11px; font-weight:600; color:#9ca3af; }
.esri-val   { font-size:13px; font-weight:800; color:#fff; white-space:nowrap; }

/* ══════════════════════════════
   HISTORY CARD
══════════════════════════════ */
.earn-hist-card {
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(167,93,237,0.13);
    border-radius:18px;
    overflow:hidden;
    position:relative;
}
.earn-hist-card::before {
    content:'';
    position:absolute;
    top:0; left:12%; right:12%; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}

.earn-hist-head {
    padding:14px 14px 12px;
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; flex-wrap:wrap;
    border-bottom:1px solid rgba(167,93,237,0.1);
}
.earn-hist-head-left { display:flex; align-items:center; gap:10px; }

.earn-filters { display:flex; align-items:center; gap:5px; flex-wrap:wrap; }
.earn-filter-btn {
    padding:5px 10px; border-radius:8px;
    font-size:10px; font-weight:700;
    color:#9ca3af;
    background:rgba(255,255,255,0.04);
    border:1px solid rgba(255,255,255,0.08);
    cursor:pointer; transition:all 0.18s;
    white-space:nowrap;
}
.earn-filter-btn:hover { background:rgba(255,255,255,0.07); color:#fff; }
.earn-filter-btn--on { color:#0f0617; font-weight:800; }

/* ── Desktop table (hidden on mobile) ── */
.earn-table-wrap { display:none; overflow-x:auto; }
@media(min-width:900px) { .earn-table-wrap { display:block; } }

.earn-table { width:100%; border-collapse:collapse; }
.earn-table thead tr { background:rgba(255,255,255,0.02); border-bottom:1px solid rgba(167,93,237,0.1); }
.earn-table th {
    padding:11px 14px;
    text-align:left;
    font-size:9px; font-weight:800;
    color:#4b5563;
    text-transform:uppercase; letter-spacing:0.1em;
    white-space:nowrap;
}
.earn-tr { border-bottom:1px solid rgba(255,255,255,0.04); transition:background 0.15s; }
.earn-tr:hover { background:rgba(167,93,237,0.04); }
.earn-tr:last-child { border-bottom:none; }
.earn-table td { padding:12px 14px; vertical-align:middle; }

.et-tx-wrap { display:flex; align-items:center; gap:10px; }
.et-tx-icon {
    width:32px; height:32px; border-radius:9px;
    display:flex; align-items:center; justify-content:center;
    flex-shrink:0;
}
.et-txid  { font-size:11px; font-weight:700; color:#e2e8f0; font-family:monospace; }
.et-txsub { font-size:9px; color:#4b5563; margin-top:2px; max-width:180px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.earn-type-badge { display:inline-block; padding:3px 9px; border-radius:6px; font-size:10px; font-weight:700; background:rgba(255,255,255,0.06); color:#e2e8f0; white-space:nowrap; }
.et-amount { font-size:13px; font-weight:800; white-space:nowrap; }
.et-fee    { font-size:11px; font-weight:600; color:#6b7280; }
.et-bal    { font-size:12px; font-weight:700; color:#e2e8f0; }
.earn-status-badge {
    display:inline-block; padding:3px 9px;
    border-radius:999px; border:1px solid;
    font-size:10px; font-weight:700;
    white-space:nowrap;
}
.et-date   { font-size:11px; font-weight:600; color:#d1d5db; white-space:nowrap; }
.et-time   { font-size:9px; color:#4b5563; margin-top:1px; }
.earn-view-btn {
    padding:4px 11px; border-radius:7px; border:1px solid;
    font-size:10px; font-weight:700;
    cursor:pointer; transition:opacity 0.18s; white-space:nowrap;
}
.earn-view-btn:hover { opacity:0.8; }

/* ── Mobile list ── */
.earn-mob-list { display:flex; flex-direction:column; }
@media(min-width:900px) { .earn-mob-list { display:none; } }

.earn-mob-item {
    padding:14px;
    border-bottom:1px solid rgba(255,255,255,0.05);
    display:flex; flex-direction:column; gap:10px;
    transition:background 0.15s;
}
.earn-mob-item:last-child { border-bottom:none; }
.earn-mob-item:hover { background:rgba(167,93,237,0.03); }
.emi-top   { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
.emi-id-wrap { flex:1; min-width:0; }
.emi-txid  { font-size:11px; font-weight:700; color:#e2e8f0; font-family:monospace; word-break:break-all; }
.emi-type  { font-size:10px; color:#4b5563; margin-top:2px; text-transform:capitalize; }
.emi-grid  { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.emi-cell  { background:rgba(255,255,255,0.03); border-radius:9px; padding:9px 10px; border:1px solid rgba(255,255,255,0.06); }
.emi-cell-lbl { font-size:9px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:4px; }
.emi-cell-val { font-size:14px; font-weight:800; }
.emi-foot  { display:flex; align-items:center; justify-content:space-between; }
.emi-date  { font-size:10px; color:#6b7280; }
.emi-fee   { font-size:10px; color:#4b5563; }
.earn-mob-view-btn {
    width:100%; padding:9px; border-radius:10px; border:1px solid;
    font-size:12px; font-weight:700; cursor:pointer;
    transition:opacity 0.18s;
}
.earn-mob-view-btn:hover { opacity:0.8; }

/* ── Empty state ── */
.earn-empty { display:flex; flex-direction:column; align-items:center; padding:40px 20px; text-align:center; }
.earn-empty-ic {
    width:56px; height:56px; border-radius:14px;
    background:rgba(167,93,237,0.08); border:1px solid rgba(167,93,237,0.16);
    display:flex; align-items:center; justify-content:center;
    color:#a75ded; margin-bottom:12px;
}
.earn-empty-t { font-size:14px; font-weight:700; color:#d1d5db; margin-bottom:4px; }
.earn-empty-s { font-size:12px; color:#4b5563; }

/* ── Pagination ── */
.earn-pagination {
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; flex-wrap:wrap;
    padding:12px 14px;
    border-top:1px solid rgba(167,93,237,0.1);
}
.earn-pag-info { font-size:11px; color:#6b7280; }
.earn-pag-info strong { color:#d1d5db; font-weight:700; }
.earn-pag-btns { display:flex; align-items:center; gap:5px; flex-wrap:wrap; }
.earn-pag-btn {
    padding:5px 11px; border-radius:8px;
    font-size:11px; font-weight:700;
    background:rgba(255,255,255,0.05);
    border:1px solid rgba(255,255,255,0.08);
    color:#d1d5db; cursor:pointer;
    transition:all 0.18s;
}
.earn-pag-btn:hover:not(:disabled) { background:rgba(255,255,255,0.09); }
.earn-pag-btn--dis { opacity:0.35; cursor:not-allowed; }
.earn-pag-num { min-width:30px; text-align:center; }

/* ══════════════════════════════
   MODAL
══════════════════════════════ */
.earn-overlay {
    position:fixed; inset:0;
    background:rgba(0,0,0,0.72);
    backdrop-filter:blur(6px);
    -webkit-backdrop-filter:blur(6px);
    z-index:50;
    display:flex; align-items:flex-end; justify-content:center;
    padding:0;
}
@media(min-width:640px) {
    .earn-overlay { align-items:center; padding:20px; }
}

.earn-modal {
    background:rgba(10,4,22,0.98);
    border:1px solid rgba(167,93,237,0.22);
    border-radius:22px 22px 0 0;
    width:100%; max-height:92vh; overflow-y:auto;
    box-shadow:0 -8px 40px rgba(0,0,0,0.6);
    position:relative;
}
.earn-modal::before {
    content:'';
    position:absolute;
    top:0; left:20%; right:20%; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.45),transparent);
}
@media(min-width:640px) {
    .earn-modal {
        max-width:640px;
        border-radius:22px;
        box-shadow:0 24px 60px rgba(0,0,0,0.7);
    }
}

.earn-modal-head {
    display:flex; align-items:center; justify-content:space-between;
    padding:16px 18px; gap:12px;
    border-bottom:1px solid rgba(167,93,237,0.12);
    position:sticky; top:0;
    background:rgba(10,4,22,0.98);
    backdrop-filter:blur(12px);
    z-index:10;
    border-radius:22px 22px 0 0;
}
@media(min-width:640px) { .earn-modal-head { border-radius:22px 22px 0 0; } }

/* Handle bar for mobile sheet */
.earn-modal-head::before {
    content:'';
    position:absolute;
    top:10px; left:50%; transform:translateX(-50%);
    width:36px; height:3px; border-radius:99px;
    background:rgba(255,255,255,0.14);
}
@media(min-width:640px) { .earn-modal-head::before { display:none; } }

.earn-modal-title { font-size:15px; font-weight:800; color:#fff; letter-spacing:-0.01em; margin-top:8px; }
@media(min-width:640px) { .earn-modal-title { margin-top:0; font-size:16px; } }

.earn-modal-close {
    width:32px; height:32px; border-radius:8px;
    display:flex; align-items:center; justify-content:center;
    background:rgba(255,255,255,0.06);
    border:1px solid rgba(255,255,255,0.08);
    color:#9ca3af; cursor:pointer; flex-shrink:0;
    transition:all 0.18s;
}
.earn-modal-close:hover { background:rgba(255,255,255,0.1); color:#fff; }

.earn-modal-body { padding:14px; display:flex; flex-direction:column; gap:12px; }
@media(min-width:640px) { .earn-modal-body { padding:18px 20px; gap:14px; } }

.earn-modal-section {
    background:rgba(255,255,255,0.025);
    border:1px solid rgba(255,255,255,0.07);
    border-radius:14px;
    padding:13px 14px;
    position:relative;
}
.ems-dot {
    width:3px; height:3px; border-radius:50%;
    display:inline-block; margin-right:8px;
    vertical-align:middle;
}
.ems-title { font-size:12px; font-weight:800; color:#fff; margin-bottom:10px; display:inline; }
.ems-grid  { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-top:10px; }
.ems-field { display:flex; flex-direction:column; gap:4px; }
.ems-lbl   { font-size:10px; font-weight:600; color:#4b5563; text-transform:uppercase; letter-spacing:0.08em; }
.ems-mono  { font-size:12px; font-weight:700; font-family:monospace; word-break:break-all; }
.ems-val   { font-size:12px; font-weight:700; color:#e2e8f0; text-transform:capitalize; }

.ems-amounts { display:flex; flex-direction:column; gap:0; margin-top:10px; }
.ems-amount-row {
    display:flex; align-items:center; justify-content:space-between;
    padding:9px 10px; gap:10px;
    border-radius:9px;
}
.ems-amount-row--hi { border-radius:9px; margin-top:4px; }

.ems-detail-list { display:flex; flex-direction:column; gap:5px; margin-top:10px; }
.ems-detail-row {
    display:flex; align-items:flex-start; justify-content:space-between;
    gap:12px; padding:8px 10px; border-radius:8px;
    background:rgba(255,255,255,0.025);
    border:1px solid rgba(255,255,255,0.06);
}
.ems-detail-key { font-size:11px; color:#6b7280; font-weight:600; flex-shrink:0; }
.ems-detail-val { font-size:11px; color:#e2e8f0; font-weight:700; text-align:right; word-break:break-all; }
.ems-raw { font-size:12px; color:#9ca3af; line-height:1.6; margin-top:8px; word-break:break-words; }

.earn-modal-foot {
    display:flex; justify-content:flex-end;
    padding:12px 16px;
    border-top:1px solid rgba(167,93,237,0.1);
    position:sticky; bottom:0;
    background:rgba(10,4,22,0.98);
    backdrop-filter:blur(12px);
}
.earn-modal-close-btn {
    padding:9px 22px; border-radius:10px;
    font-size:12px; font-weight:700; color:#fff;
    background:rgba(255,255,255,0.07);
    border:1px solid rgba(255,255,255,0.1);
    cursor:pointer; transition:all 0.18s;
}
.earn-modal-close-btn:hover { background:rgba(255,255,255,0.12); }

/* ══════════════════════════════
   MODAL TRANSITIONS
══════════════════════════════ */
.earn-fade-enter-active, .earn-fade-leave-active { transition:opacity 0.25s; }
.earn-fade-enter-from, .earn-fade-leave-to { opacity:0; }

.earn-pop-enter-active  { transition:all 0.3s cubic-bezier(0.22,1,0.36,1); }
.earn-pop-leave-active  { transition:all 0.2s ease-in; }
.earn-pop-enter-from    { opacity:0; transform:translateY(30px); }
.earn-pop-leave-to      { opacity:0; transform:translateY(20px); }
@media(min-width:640px) {
    .earn-pop-enter-from { transform:scale(0.95) translateY(0); }
    .earn-pop-leave-to   { transform:scale(0.95) translateY(0); }
}

/* ══════════════════════════════
   SCROLLBAR
══════════════════════════════ */
::-webkit-scrollbar { height:5px; width:5px; }
::-webkit-scrollbar-track { background:rgba(30,41,59,0.2); border-radius:10px; }
::-webkit-scrollbar-thumb { background:rgba(167,93,237,0.3); border-radius:10px; }
::-webkit-scrollbar-thumb:hover { background:rgba(167,93,237,0.5); }
</style>
